import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  ChangeDetectorRef,
  NgZone,
} from '@angular/core';
import { MatTreeModule } from '@angular/material/tree';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { lastValueFrom, WebService } from '@services';
import { RmsModule } from '@rms';

type ApiType = 'file' | 'directory';

interface ApiItem {
  id?: string;
  name: string;
  type: ApiType;               // 'file' | 'directory'
  path: string;                // full path (e.g., Azure prefix)
  created?: string | Date;
}

/** Flat node for MatTree (no outlet) */
interface FlatNode {
  id?: string;
  name: string;
  type: 'file' | 'folder';
  path: string;
  created?: string | Date;

  level: number;               // 0=root, 1=child, ...
  expandable: boolean;         // true for folder
  parentPath?: string | null;  // to compute subtree removal

  loaded?: boolean;            // have we loaded children yet?
  _loading?: boolean;          // spinner for this row
  _expanded?: boolean;         // current expansion state
}

interface PreviewState {
  node: FlatNode;
  mime: string;
  url?: string;                // blob: or direct http(s)
  safeUrl?: SafeResourceUrl;   // for <object>/<iframe>
  text?: string;               // for text previews
  loading: boolean;
  error?: string;
}

@Component({
  selector: 'app-file-explorer',
  standalone: true,
  imports: [MatTreeModule, MatIconModule, MatButtonModule, MatProgressSpinnerModule, RmsModule],
  templateUrl: './file-explorer.component.html',
  styleUrl: './file-explorer.component.scss',
})
export class FileExplorerComponent implements OnInit, OnDestroy {
  /** Root folder/prefix (e.g., "/purchase-orders/PO-123/INV-456/") */
  @Input() rootPath?: string;

  /** Pane sizing if you wrap with <rms-mod> in the parent */
  @Input() leftWidth = '30vw';
  @Input() rightWidth = '65vw';
  @Input() height = '85vh';

  /** Flat data source (array of rows) */
  flatData: FlatNode[] = [];

  /** v17: use levelAccessor (no NestedTreeControl). */
  levelAccessor = (n: FlatNode) => n.level;

  /** TrackBy for performance */
  trackByPath = (i: number, n: FlatNode) => n.path;

  /** Preview */
  preview?: PreviewState;

  constructor(
    private web: WebService,
    private sanitizer: DomSanitizer,
    private cdr: ChangeDetectorRef,
    private zone: NgZone
  ) { }

  // ---------------- Lifecycle ----------------
  async ngOnInit() {
    await this.loadRoot();
  }

  ngOnDestroy() {
    this.revokePreviewUrl();
  }

  private async loadRoot() {
    const items = await this.fetchList(this.rootPath ?? undefined);
    this.flatData = items.map(api => this.toFlat(api, 0, null));
    this.cdr.detectChanges(); // stamp the tree
  }

  // ---------------- API ----------------
  private async fetchList(path?: string): Promise<ApiItem[]> {
    const endpoint = path ? `/list?path=${encodeURIComponent(path)}` : '/list';
    return await lastValueFrom(this.web.getWebCall('files', endpoint));
  }

  private async fetchFileBlob(path: string): Promise<Blob> {
    const endpoint = `/download?path=${encodeURIComponent(path)}`;
    return await lastValueFrom(
      this.web.getWebCall('files', endpoint, { responseType: 'blob' })
    );
  }

  private async fetchFileUrl(path: string): Promise<string> {
    const endpoint = `/signed-url?path=${encodeURIComponent(path)}`;
    const resp: any = await lastValueFrom(this.web.getWebCall('files', endpoint));
    return resp?.url as string;
  }

  // ---------------- Normalization ----------------
  private toFlat(api: ApiItem, level: number, parentPath: string | null): FlatNode {
    const isFolder = api.type === 'directory';
    return {
      id: api.id,
      name: api.name,
      path: api.path,
      created: api.created,
      type: isFolder ? 'folder' : 'file',
      level,
      expandable: isFolder,
      parentPath,
      loaded: false,
      _expanded: false,
    };
  }

  // ---------------- Expansion / Lazy load (flat) ----------------
  /**
   * Called by (expandedChange) on folder rows.
   * - On expand: if first time, load children, insert them into flatData right after the parent row
   * - On collapse: remove all descendant rows (until a row with <= parent.level)
   */
  async onFlatExpanded(node: FlatNode, expanded: boolean): Promise<void> {
    node._expanded = expanded;

    const parentIndex = this.indexOfPath(node.path);
    if (parentIndex < 0) return;

    if (!expanded) {
      // Remove the subtree rows
      const end = this.findSubtreeEndIndex(parentIndex);
      if (end > parentIndex) {
        this.flatData.splice(parentIndex + 1, end - parentIndex - 1);
        this.reemitFlatData();
      }
      return;
    }

    // Expanded
    try {
      node._loading = true;

      const items = await this.fetchList(node.path);
      const children = items
        .map(api => this.toFlat(api, node.level + 1, node.path))
        .sort((a, b) =>
          a.type === b.type ? a.name.localeCompare(b.name) : (a.type === 'folder' ? -1 : 1)
        );

      // Insert children right after parent row
      this.flatData.splice(parentIndex + 1, 0, ...children);

      node.loaded = true;
    } catch (err) {
      console.error('[Tree] load children failed', node.path, err);
    } finally {
      node._loading = false;
      this.reemitFlatData();
    }
  }

  /** Find index of node by path */
  private indexOfPath(path: string): number {
    return this.flatData.findIndex(n => n.path === path);
  }

  /**
   * Find the end index of this node's subtree.
   * Scans forward until a row with level <= parent.level
   */
  private findSubtreeEndIndex(parentIndex: number): number {
    const parentLevel = this.flatData[parentIndex].level;
    let i = parentIndex + 1;
    while (i < this.flatData.length && this.flatData[i].level > parentLevel) i++;
    return i;
  }

  /** Immutable "poke" + flush in-zone to re-render the tree */
  private reemitFlatData(): void {
    this.zone.run(() => {
      this.flatData = [...this.flatData];
      this.cdr.detectChanges();
    });
  }

  // ---------------- Preview ----------------
  onPreview(node: FlatNode): void {
    if (node.type !== 'file') return;
    const mime = this.guessMime(node.name);
    this.preview = { node, mime, loading: true };

    this.loadPreview(node, mime).catch(err => {
      console.error('[Preview] failed', err);
      this.preview = { node, mime, loading: false, error: 'Failed to load preview' };
    });
  }
  closePreview(): void {
    this.preview = undefined;
  }
  private revokePreviewUrl() {
    const u = this.preview?.url;
    if (u && u.startsWith('blob:')) URL.revokeObjectURL(u);
  }

  private async loadPreview(node: FlatNode, mime: string): Promise<void> {
    // Try Office Web Viewer if possible
    if (this.isOfficeMime(mime)) {
      const directUrl = await this.tryGetDirectUrl(node.path);
      if (directUrl) {
        const officeViewUrl =
          `https://view.officeapps.live.com/op/view.aspx?src=${encodeURIComponent(directUrl)}`;
        this.revokePreviewUrl();
        this.preview = {
          node,
          mime,
          url: directUrl,
          safeUrl: this.sanitizer.bypassSecurityTrustResourceUrl(officeViewUrl),
          loading: false,
        };
        return;
      }
    }

    // Fallback: fetch blob
    const blob = await this.fetchFileBlob(node.path);
    this.revokePreviewUrl();

    const blobUrl = URL.createObjectURL(blob);
    const contentType = blob.type || mime;

    if (contentType === 'application/pdf' || mime === 'application/pdf') {
      this.preview = {
        node,
        mime: 'application/pdf',
        url: blobUrl,
        safeUrl: this.sanitizer.bypassSecurityTrustResourceUrl(blobUrl),
        loading: false,
      };
      return;
    }

    if (this.isTextMime(mime)) {
      const text = await blob.text();
      this.preview = { node, mime, text, url: blobUrl, loading: false };
      return;
    }

    this.preview = { node, mime, url: blobUrl, loading: false };
  }

  private async tryGetDirectUrl(path: string): Promise<string | null> {
    try {
      const url = await this.fetchFileUrl(path);
      return url || null;
    } catch {
      return null;
    }
  }

  // ---------------- Download ----------------
  async onDownload(): Promise<void> {
    if (!this.preview) return;
    const node = this.preview.node;

    try {
      if (this.preview.url?.startsWith('blob:')) {
        this.triggerBrowserDownload(this.preview.url, this.getDownloadName());
        return;
      }
      const blob = await this.fetchFileBlob(node.path);
      const url = URL.createObjectURL(blob);
      this.triggerBrowserDownload(url, this.getDownloadName());
    } catch (err) {
      console.error('[Download] failed', err);
    }
  }

  private triggerBrowserDownload(url: string, filename: string): void {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'download';
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  getDownloadName(): string {
    return this.preview?.node?.name ?? 'download';
  }

  // ---------------- MIME / Icons ----------------
  private getExt(name: string): string {
    const lastDot = name.lastIndexOf('.');
    return lastDot >= 0 ? name.substring(lastDot + 1).toLowerCase() : '';
  }

  getFileIconCfg(node: FlatNode): { icon: string; colorClass?: string } {
    if (node.type === 'folder') return { icon: 'folder', colorClass: 'icon-folder' };
    const ext = this.getExt(node.name);
    switch (ext) {
      case 'pdf': return { icon: 'picture_as_pdf', colorClass: 'icon-pdf' };
      case 'doc': case 'docx': return { icon: 'description', colorClass: 'icon-word' };
      case 'xls': case 'xlsx': return { icon: 'table_view', colorClass: 'icon-excel' };
      case 'ppt': case 'pptx': return { icon: 'slideshow', colorClass: 'icon-ppt' };
      case 'txt': case 'log': return { icon: 'text_snippet', colorClass: 'icon-text' };
      case 'csv': return { icon: 'grid_on', colorClass: 'icon-csv' };
      case 'json': return { icon: 'data_object', colorClass: 'icon-json' };
      case 'md': return { icon: 'article', colorClass: 'icon-markdown' };
      case 'png': case 'jpg': case 'jpeg': case 'gif': case 'webp': return { icon: 'image', colorClass: 'icon-image' };
      case 'mp3': case 'wav': case 'flac': return { icon: 'audio_file', colorClass: 'icon-audio' };
      case 'mp4': case 'mov': case 'avi': case 'webm': return { icon: 'video_file', colorClass: 'icon-video' };
      case 'zip': case 'rar': case '7z': case 'gz': case 'tar': return { icon: 'archive', colorClass: 'icon-archive' };
      default: return { icon: 'insert_drive_file', colorClass: 'icon-generic' };
    }
  }

  private guessMime(filename: string): string {
    const ext = (filename.split('.').pop() || '').toLowerCase();
    switch (ext) {
      case 'pdf': return 'application/pdf';
      case 'png': case 'jpg': case 'jpeg': case 'gif': case 'webp':
        return `image/${ext === 'jpg' ? 'jpeg' : ext}`;
      case 'txt': case 'log': return 'text/plain';
      case 'csv': return 'text/csv';
      case 'json': return 'application/json';
      case 'md': return 'text/markdown';
      case 'doc': return 'application/msword';
      case 'docx': return 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      case 'xls': return 'application/vnd.ms-excel';
      case 'xlsx': return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      case 'ppt': return 'application/vnd.ms-powerpoint';
      case 'pptx': return 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
      default: return 'application/octet-stream';
    }
  }

  private isTextMime(mime: string): boolean {
    return mime.startsWith('text/') || ['application/json', 'text/markdown'].includes(mime);
  }

  private isOfficeMime(mime: string): boolean {
    return mime.startsWith('application/vnd.openxmlformats')
      || mime === 'application/msword'
      || mime === 'application/vnd.ms-excel'
      || mime === 'application/vnd.ms-powerpoint';
  }

  // ---------------- Template predicates (flat) ----------------
  isFlatFolder = (_: number, node: FlatNode) => node.type === 'folder';
  isFlatFile = (_: number, node: FlatNode) => node.type === 'file';

  // ---------------- Preview guards used in template ----------------
  hasPreview(): boolean { return !!this.preview; }
  isLoadingPreview(): boolean { return !!this.preview?.loading; }
  hasErrorPreview(): boolean { return !!this.preview?.error && !this.preview?.loading; }
  getPreviewTitle(): string { return this.preview?.node?.name ?? ''; }
  getPreviewMime(): string { return this.preview?.mime ?? ''; }

  isPdfPreview(): boolean {
    const p = this.preview;
    return !!p && !p.loading && !!p.safeUrl && p.mime === 'application/pdf';
  }
  isImagePreview(): boolean {
    const p = this.preview;
    return !!p && !p.loading && !!p.url && p.mime?.startsWith('image/');
  }
  isTextPreview(): boolean {
    const p = this.preview, m = p?.mime;
    return !!p && !p.loading && !!p.text && !!m &&
      (m.startsWith('text/') || m === 'application/json' || m === 'text/markdown');
  }
  isOfficePreview(): boolean {
    const p = this.preview;
    return !!p && !p.loading && !!p.safeUrl && !!p.mime &&
      (p.mime.startsWith('application/vnd.openxmlformats')
        || p.mime === 'application/msword'
        || p.mime === 'application/vnd.ms-excel'
        || p.mime === 'application/vnd.ms-powerpoint');
  }
  isFallbackPreview(): boolean {
    const p = this.preview;
    if (!p || p.loading || p.error) return false;
    const known = this.isPdfPreview() || this.isImagePreview() || this.isTextPreview() || this.isOfficePreview();
    return !known;
  }
}
